/**
 * SchrittListe — die Checkliste „was fehlt noch" im Detail (Konzept A, Inhalt).
 *
 * @description
 *   Zeigt die vier Schritte aus `schritte.js` untereinander. Der ERSTE offene
 *   Schritt trägt seine Handlung selbst; ein BLOCKIERTER Schritt trägt seinen
 *   Grund und seinen Weg, aber KEINEN Knopf (der tote Knopf, den Konzept A
 *   abgeschafft hat). Der Grund steht genau EINMAL im DOM; der Schritt ist eine
 *   `group` mit `aria-labelledby`/`aria-describedby`, damit ein Screenreader
 *   Titel und Grund zusammen vorliest (R16-C5).
 *
 * @useWhen
 *   - Das Detail eines Falls zeigt, was als Nächstes zu tun ist
 *
 * @dontUseFor
 *   - Eine Server-Wahrheit (Bestand/Kanal/Live-Preis) — die prüft der Server
 *   - Die Liste links: die zeigt nur `kurz` aus derselben Ableitung
 *
 * @related
 *   - `schritte.js` — die Ableitung
 *   - `FallDetail.jsx` — der Rahmen
 *
 * @props
 *   schritte   Das Ergebnis von `schritte(zeile, vorschau)`
 *   idBasis    Präfix für die Grund-`id`s (eindeutig je Detail)
 *   knopfFuer  (schritt) => ReactNode — die Handlung des ersten offenen Schritts
 *   inhaltFuer (schritt) => ReactNode — z. B. das Rabattfeld im `rabatt`-Schritt
 *   wegFuer    (schritt) => ReactNode — der Weg aus einer Blockade (R16-B4)
 *   gedaempft  true = Ladezustand: keine Knöpfe, gedämpfte Zeilen (R16-C3)
 */
import { Box, Stack, Typography } from '@mui/material';
import { SP, FS } from '../../theme/tokens';
import { v2Tone } from '../../theme/theme';

const LAGE_TEXT = {
  erledigt: 'Done',
  offen: 'Next',
  blockiert: 'Blocked',
  wartet: 'Waiting',
};

const LAGE_TON = {
  erledigt: 'success',
  offen: 'primary',
  blockiert: 'warn',
  wartet: 'plain',
};

// 🔴 **R16-W1: die Einrückung ist EIN Maß, nicht vier Literale.** Die vier
// `pl: EINZUG` hingen an der Labelbreite (5.5em) plus einem halben em; änderte
// sich `FS.xs` oder der Text, drifteten sie still auseinander.
const LABEL_SPALTE = '5.5em';
const EINZUG = `calc(${LABEL_SPALTE} + 0.5em)`;

export default function SchrittListe({
  schritte = [], idBasis = 'schritt', knopfFuer, inhaltFuer, wegFuer, gedaempft = false,
}) {
  // 🔴 **Nur der ERSTE offene Schritt trägt einen Knopf** (R15-W5): ein Entwurf
  // ohne Rabatt und ohne Text hat zwei offene Schritte — zwei Hauptknöpfe für
  // eine Reihenfolge. Der Knopf gehört dem, der zuerst dran ist.
  const ersterOffen = schritte.findIndex((s) => s.lage === 'offen');
  return (
    <Stack spacing={SP.tight} component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {schritte.map((s, i) => {
        const grundId = `${idBasis}-${s.key}-grund`;
        const titelId = `${idBasis}-${s.key}-titel`;
        const mitKnopf = !gedaempft && i === ersterOffen && !!knopfFuer;
        return (
          <Box component="li" key={s.key} data-testid={`schritt-${s.key}`}>
            <Box
              role="group"
              aria-labelledby={titelId}
              aria-describedby={s.grund ? grundId : undefined}
              sx={{ opacity: gedaempft ? 0.55 : 1 }}
            >
              <Stack direction="row" spacing={SP.chip} alignItems="baseline">
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: (t) => (LAGE_TON[s.lage] === 'warn' ? v2Tone(t, 'warn').main : 'text.secondary'),
                    minWidth: LABEL_SPALTE,
                    fontSize: FS.xs,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  {LAGE_TEXT[s.lage] || s.lage}
                </Typography>
                <Typography id={titelId} variant="body2" sx={{ fontWeight: s.lage === 'offen' ? 600 : 400 }}>
                  {s.titel}
                </Typography>
              </Stack>
              {s.grund && (
                <Typography id={grundId} variant="caption" sx={{ color: (t) => v2Tone(t, 'warn').main, display: 'block', pl: EINZUG }}>
                  {s.grund}
                </Typography>
              )}
              {s.hinweis && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: EINZUG }}>
                  {s.hinweis}
                </Typography>
              )}
              {wegFuer && wegFuer(s) ? (
                <Box sx={{ pl: EINZUG, pt: SP.chip }}>{wegFuer(s)}</Box>
              ) : null}
              {inhaltFuer && inhaltFuer(s) ? (
                <Box sx={{ pl: EINZUG, pt: SP.chip }}>{inhaltFuer(s)}</Box>
              ) : null}
              {mitKnopf ? (
                <Box sx={{ pl: EINZUG, pt: SP.chip }}>{knopfFuer(s)}</Box>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}
